// Utility functions for RMS components

// The kind vocabulary used to be defined a second time here, and the two copies
// had already drifted — this one called a vehicle an "Investment Vehicle" while
// Dashboard's called it a "Vehicle", so the name a user saw depended on which
// screen they were on. There is now one definition; this re-export keeps the
// existing import paths working (#11238).
export { entityKindMap } from "./Dashboard/utils";
export type { EntityKindInfo } from "./Dashboard/utils";

/**
 * Formats a number as a dollar amount with appropriate suffix (K, M, B, T)
 */
export function formatDollarAmount(amount: number): string {
  if (amount === 0) return "$0";

  const absAmount = Math.abs(amount);
  const sign = amount < 0 ? "-" : "";

  if (absAmount >= 1e12) {
    return `${sign}$${(absAmount / 1e12).toFixed(1)}T`;
  } else if (absAmount >= 1e9) {
    return `${sign}$${(absAmount / 1e9).toFixed(1)}B`;
  } else if (absAmount >= 1e6) {
    return `${sign}$${(absAmount / 1e6).toFixed(1)}M`;
  } else if (absAmount >= 1e3) {
    return `${sign}$${(absAmount / 1e3).toFixed(1)}K`;
  } else {
    return `${sign}$${absAmount.toFixed(0)}`;
  }
}

/**
 * Formats a number as the whole dollar figure, for the full-amount unit
 * (`usd_full`): comma grouping, no decimals for whole dollars, exactly two
 * when there are cents, never a K/M/B/T suffix.
 * `$10,000,232`, `$1,250.50`, `-$1,250.50`.
 */
export function formatDollarFull(amount: number): string {
  const sign = amount < 0 ? "-" : "";
  const cents = !Number.isInteger(amount);
  return `${sign}$${Math.abs(amount).toLocaleString("en-US", {
    minimumFractionDigits: cents ? 2 : 0,
    maximumFractionDigits: cents ? 2 : 0,
  })}`;
}

/**
 * Formats a number as a percentage with full precision
 */
export function formatPercentAmount(amount: number): string {
  if (amount === 0) return "0%";

  // Show full precision without truncation
  return `${amount}%`;
}

/**
 * Truncates text to specified length with ellipsis
 */
export function truncateText(text: string, maxLength: number = 50): string {
  if (typeof text !== "string") return String(text || "");
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + "...";
}
